import React, { useEffect, useMemo, useRef, useState } from "react";

export type ComboOption = { value: string; label: string; sub?: string };

/**
 * A searchable single-select — the unit picker. A native <select> stops being usable somewhere
 * around a dozen trucks, so this trades it for a filterable popover while keeping the button
 * footprint of the other toolbar controls.
 *
 * `size="lg"` is the topbar's build: the same control at the height of the search field and the
 * theme and account buttons it stands next to, so that row reads as one bar.
 *
 * `action` is one row pinned under the list — "+ Add new organization" — for the thing you do
 * when none of the options is the one you want. It sits outside the filter, so it is there
 * whatever was typed, including when nothing matches.
 */
const POPOVER_MIN_WIDTH = 260;

const SIZES = {
    sm: "min-h-9 gap-2.5 rounded-[10px] px-3 text-[12.5px]",
    lg: "min-h-[46px] gap-3 rounded-[13px] px-4 text-[13.5px]",
};

export const Combo: React.FC<{
    options: ComboOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    size?: keyof typeof SIZES;
    /** Names the control for screen readers where no visible label sits beside it. */
    ariaLabel?: string;
    className?: string;
    /** A row under the list that runs something instead of picking a value. */
    action?: { label: string; onSelect: () => void };
}> = ({ options, value, onChange, placeholder = "Search…", size = "sm", ariaLabel, className = "", action }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [cursor, setCursor] = useState(0);
    // Which edge the popover hangs from. It is 260px wide and opens under the button's left edge,
    // which runs off a phone's screen for any picker sitting on the right of a toolbar — and an
    // element past the right edge is exactly what gives the page a sideways scroll. Measured on
    // open rather than configured: the same picker sits left in the topbar and right in a TableBar.
    const [alignRight, setAlignRight] = useState(false);
    const wrapRef = useRef<HTMLSpanElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const shown = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => `${o.label} ${o.sub ?? ""}`.toLowerCase().includes(q));
    }, [options, query]);

    useEffect(() => {
        if (!open) return;
        setQuery("");
        setCursor(Math.max(0, options.findIndex((o) => o.value === value)));
        const rect = wrapRef.current?.getBoundingClientRect();
        setAlignRight(Boolean(rect && rect.left + POPOVER_MIN_WIDTH > window.innerWidth - 16));
        searchRef.current?.focus();
        const onDoc = (e: MouseEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const pick = (v: string) => {
        onChange(v);
        setOpen(false);
    };

    const current = options.find((o) => o.value === value);

    return (
        <span ref={wrapRef} className={`relative inline-flex ${className}`}>
            <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
                onClick={() => setOpen(!open)}
                className={`inline-flex w-full items-center justify-between whitespace-nowrap border border-[var(--line-2)] bg-[var(--surface)] font-medium ${SIZES[size]}`}
            >
                <span className="min-w-0 truncate">{current?.label ?? value}</span>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2.2" aria-hidden className="flex-none">
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>
            {open && (
                <span
                    className={`absolute top-[calc(100%+6px)] z-30 min-w-[260px] max-w-[calc(100vw-32px)] rounded-[13px] border border-[var(--line-2)] bg-[var(--surface)] p-2 shadow-[0_24px_60px_rgba(0,0,0,.5)] ${
                        alignRight ? "right-0" : "left-0"
                    }`}
                >
                    <input
                        ref={searchRef}
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setCursor(0);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                                e.preventDefault();
                                setCursor((c) => Math.max(0, Math.min(shown.length - 1, c + (e.key === "ArrowDown" ? 1 : -1))));
                            } else if (e.key === "Enter") {
                                e.preventDefault();
                                if (shown[cursor]) pick(shown[cursor].value);
                            } else if (e.key === "Escape") {
                                setOpen(false);
                            }
                        }}
                        placeholder={placeholder}
                        className="mb-1.5 w-full rounded-[9px] border border-[var(--line-2)] bg-[var(--surface-2)] px-3 py-2 text-[13px] outline-none focus:border-[var(--accent)]"
                    />
                    <span className="block max-h-[260px] overflow-y-auto" role="listbox">
                        {shown.length === 0 && <span className="block px-3 py-3 text-[12.5px] text-[var(--muted)]">Nothing matches that</span>}
                        {shown.map((o, i) => (
                            <button
                                key={o.value}
                                type="button"
                                role="option"
                                aria-selected={o.value === value}
                                onClick={() => pick(o.value)}
                                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] ${
                                    i === cursor ? "bg-[var(--surface-2)]" : ""
                                } ${o.value === value ? "font-semibold text-[var(--accent)]" : ""}`}
                            >
                                {o.label}
                                {o.sub && <small className="ml-auto text-[11.5px] text-[var(--faint)]">{o.sub}</small>}
                            </button>
                        ))}
                    </span>
                    {action && (
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(false);
                                action.onSelect();
                            }}
                            className="mt-1.5 flex w-full items-center gap-2 rounded-lg border-t border-[var(--line)] px-2.5 pb-1.5 pt-2.5 text-left text-[13px] font-semibold text-[var(--accent)] hover:bg-[var(--surface-2)]"
                        >
                            {action.label}
                        </button>
                    )}
                </span>
            )}
        </span>
    );
};
